import { ApiError } from '@/lib/api'
import { markUserActivity } from '@/lib/presence'
import {
  RPC_ID_PATTERN,
  RPC_MAX_IN_FLIGHT,
  isRpcMethod,
  type RpcError,
  type RpcEvent,
  type RpcEventName,
  type RpcEvents,
  type RpcMethod,
  type RpcReply,
} from './contract'
import { RPC_TABLE, RpcParamError, parseParams } from './methods'

/* ------------------------------------------------------------------
   پلِ RPC ِ پنل برای قابِ گفتگو — قرارداد در `contract.ts`
   ------------------------------------------------------------------
   🔴 چرا پنل پاسخ می‌دهد و نه خودِ چت به API.

   قاب دامنهٔ دیگری است و کوکیِ نشست و CSRF ِ پنل را ندارد. پنل
   درخواستِ قاب را با **نشستِ خودش** به API می‌برد؛ پس هر پاسخ دادهٔ
   کاربرِ پنل است و فقط به قابی می‌رود که همان کاربر در آن است.

   سه دروازه، به ترتیب:
   1. پیام از مبدأِ چت **و** از همین قاب (`event.source`) آمده باشد —
      پنجرهٔ دیگری از همان دامنه (مثلاً پنجرهٔ بازشده از چت) پاسخ نمی‌گیرد.
   2. کاربرِ قاب با ایمیل «تأییدشده» باشد (`setVerified`)؛ وگرنه `not_verified`.
   3. متد در جدولِ ثابت و `params` به شکلِ آن (`methods.ts`).

   🔴 `{carbonActivity}` هم از همین پل می‌گذرد: «کاربر در قاب کار کرد» —
   برای «آنلاین روی سایت» (`lib/presence.ts`)، با همان دو دروازهٔ اول.

   ⚠️ تأیید ممکن است وسطِ یک درخواست از دست برود (خروج، یا کسِ دیگری در
   قاب). هر درخواست «دوره»ٔ تأییدِ خودش را نگه می‌دارد و اگر تا رسیدنِ
   پاسخ دوره عوض شده باشد، به‌جای داده `not_verified` می‌رود — دادهٔ
   کاربرِ پنل هرگز به قابِ کسِ دیگری نمی‌رسد.
   ------------------------------------------------------------------ */

export interface ChatRpcBridge {
  /** پیامِ `carbonRpc` بود (پاسخ داده شد یا رد شد) → `true`؛ پیامِ دیگری → `false`. */
  handle(event: MessageEvent): boolean
  /** تأییدِ کاربرِ قاب عوض شد. با `true` رویدادِ `session.verified` هم می‌رود. */
  setVerified(value: boolean): void
  push<N extends RpcEventName>(name: N, data: RpcEvents[N]): void
  dispose(): void
}

interface BridgeOptions {
  /** مبدأِ چت — همان `new URL(chatUrl).origin` */
  origin: string
  /** پنجرهٔ قاب؛ با هر بارگذاریِ قاب همان WindowProxy می‌ماند. */
  frame: () => Window | null | undefined
}

const NOT_VERIFIED: RpcError = {
  code: 'not_verified',
  message: 'حسابِ گفتگو هنوز با حسابِ پنل تطبیق داده نشده است؛ چند لحظهٔ دیگر دوباره امتحان کنید.',
}

function toRpcError(error: unknown): RpcError {
  if (error instanceof RpcParamError) {
    return { code: 'bad_params', message: error.message, fields: error.fields }
  }

  if (error instanceof ApiError) {
    return {
      code: error.code,
      message: error.message,
      status: error.status,
      ...(error.fields.length > 0 ? { fields: error.fields.map((f) => ({ ...f })) } : {}),
      ...(error.requestId ? { requestId: error.requestId } : {}),
    }
  }

  return { code: 'internal', message: 'درخواست در پنل انجام نشد.' }
}

export function createChatRpcBridge({ origin, frame }: BridgeOptions): ChatRpcBridge {
  let verified = false
  let epoch = 0
  let inFlight = 0
  let disposed = false

  const post = (message: object) => {
    if (disposed) return
    try {
      frame()?.postMessage(message, origin)
    } catch {
      /* قاب در حالِ رفتن است — پاسخ گم می‌شود و کلاینت با timeout رد می‌کند. */
    }
  }

  const reply = (value: RpcReply) => post({ carbonRpcResult: value })

  const push = <N extends RpcEventName>(name: N, data: RpcEvents[N]) => {
    if (!verified) return
    post({ carbonEvent: { name, data } satisfies RpcEvent<N> })
  }

  async function run(id: string, method: RpcMethod, rawParams: unknown): Promise<void> {
    const startedIn = epoch
    const stillTrusted = () => !disposed && verified && epoch === startedIn

    let params: Record<string, unknown>
    try {
      params = parseParams(rawParams, RPC_TABLE[method].rules)
    } catch (error) {
      reply({ id, ok: false, error: toRpcError(error) })
      return
    }

    /*
      ⚠️ نوعِ هر خانهٔ جدول به متدِ خودش بسته است؛ این‌جا متد پویاست، پس
      TypeScript آن را «اجتماعِ همه» می‌بیند. شکلِ واقعی را `parseParams`
      همین بالا تضمین کرده است.
    */
    const spec = RPC_TABLE[method] as unknown as {
      call(p: Record<string, unknown>): Promise<unknown>
      events?(p: Record<string, unknown>, result: unknown): RpcEvent[]
      activity?: boolean
    }

    /*
      ⚠️ نوشتن (ساختن، ویرایش، چیدنِ اعضا) همیشه کارِ خودِ کاربر است و پیش
      از رفتنِ درخواست ثبت می‌شود تا همان درخواست `active` برود. خواندن
      ممکن است پرسشِ زمان‌دارِ رابط باشد و حضور حساب نمی‌شود — `chat.seen`
      هر ۱۲ ثانیه می‌پرسد و کسی را «آنلاین» نگه نمی‌دارد. ساختنِ گروه رویدادی
      ندارد (خودِ Rocket.Chat اتاقِ تازه را می‌رساند)، پس با `activity`.
    */
    if (spec.events || spec.activity) markUserActivity()

    inFlight++
    try {
      const data = await spec.call(params)
      if (!stillTrusted()) {
        reply({ id, ok: false, error: NOT_VERIFIED })
        return
      }
      reply({ id, ok: true, data } as RpcReply)
      for (const event of spec.events?.(params, data) ?? []) push(event.name, event.data)
    } catch (error) {
      reply({ id, ok: false, error: stillTrusted() ? toRpcError(error) : NOT_VERIFIED })
    } finally {
      inFlight--
    }
  }

  return {
    handle(event) {
      if (event.origin !== origin) return false

      const data = event.data as { carbonRpc?: unknown; carbonActivity?: unknown } | null
      if (!data || typeof data !== 'object') return false

      /*
        🔴 کاربر داخلِ قابِ گفتگو کار می‌کند — پنل خودش آن کلیک و کلید را
        نمی‌بیند (رویدادها مالِ سندِ قاب‌اند). بی این، کسی که یک ساعت در
        چت می‌نویسد پس از چند دقیقه «آفلاین» دیده می‌شد (`lib/presence.ts`).

        ⚠️ همان دو دروازهٔ RPC: از همین قاب و با کاربرِ تأییدشده. چیزی جز
        «همین الان» با خود نمی‌آورد و پاسخی هم ندارد.
      */
      if ('carbonActivity' in data) {
        if (verified && event.source === frame()) markUserActivity()
        return true
      }

      if (!('carbonRpc' in data)) return false

      /* پیامِ RPC است — از این‌جا به بعد مالِ همین پل است، حتی اگر رد شود. */
      const source = frame()
      if (!source || event.source !== source) return true

      const request = data.carbonRpc as { id?: unknown; method?: unknown; params?: unknown } | null
      if (!request || typeof request !== 'object') return true

      const id = request.id
      if (typeof id !== 'string' || !RPC_ID_PATTERN.test(id)) return true

      if (!verified) {
        reply({ id, ok: false, error: NOT_VERIFIED })
        return true
      }

      if (!isRpcMethod(request.method)) {
        reply({
          id,
          ok: false,
          error: { code: 'unknown_method', message: `متدِ «${String(request.method).slice(0, 64)}» در پنل نیست.` },
        })
        return true
      }

      if (inFlight >= RPC_MAX_IN_FLIGHT) {
        reply({ id, ok: false, error: { code: 'busy', message: 'درخواست‌های زیادی در راه است؛ کمی بعد دوباره.' } })
        return true
      }

      void run(id, request.method, request.params)
      return true
    },

    setVerified(value) {
      if (value === verified) return
      verified = value
      epoch++
      if (value) push('session.verified', {})
    },

    push,

    dispose() {
      disposed = true
      verified = false
      epoch++
    },
  }
}
